import { useLayoutEffect, useRef, useState } from "react";

/**
 * Detects whether an element's text is visually truncated — either by a
 * single-line ellipsis (`text-overflow: ellipsis`) or a multi-line clamp
 * (`-webkit-line-clamp`). Returns `[ref, isTruncated]`; attach the ref to the
 * text element and the flag updates on resize. Pass the rendered text as `dep`
 * so the check re-runs when the content changes.
 *
 * Use it to show a tooltip only when the text is actually clipped, and hide it
 * when the full text is already visible.
 */
export function useIsTruncated<T extends HTMLElement = HTMLElement>(
  dep?: unknown,
) {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      setIsTruncated(
        el.scrollWidth > el.clientWidth + 1 ||
          el.scrollHeight > el.clientHeight + 1,
      );
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    // Web fonts (e.g. Google Sans from the CDN) load asynchronously and change
    // text metrics after the first measure — without resizing the clamped box,
    // so the ResizeObserver won't re-fire. Re-check once fonts are ready.
    document.fonts?.ready.then(check).catch(() => {});
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [dep]);

  return [ref, isTruncated] as const;
}

export default useIsTruncated;
